
import { JavaCourse } from './types';

const JAVA_INTERMEDIATE_MODULES = [
  {
    id: 'java-collections',
    title: 'Collections Framework',
    description: 'Master Java collections including Lists, Sets, Maps, and advanced collection operations.',
    content: `
# Java Collections Framework

The Java Collections Framework provides a comprehensive set of interfaces and classes for storing and manipulating groups of objects.

## Core Interfaces

### List Interface
\`\`\`java
// ArrayList - Dynamic array
List<String> arrayList = new ArrayList<>();
arrayList.add("Java");
arrayList.add("Python");
arrayList.add("JavaScript");

// LinkedList - Doubly-linked list
List<Integer> linkedList = new LinkedList<>();
linkedList.add(1);
linkedList.add(2);
\`\`\`

### Set Interface
\`\`\`java
// HashSet - No duplicates, no order
Set<String> hashSet = new HashSet<>();
hashSet.add("Apple");
hashSet.add("Banana");
hashSet.add("Apple"); // Duplicate, won't be added

// TreeSet - Sorted set
Set<Integer> treeSet = new TreeSet<>();
treeSet.add(5);
treeSet.add(1);
treeSet.add(3);
// Output will be: [1, 3, 5]
\`\`\`

### Map Interface
\`\`\`java
// HashMap - Key-value pairs
Map<String, Integer> scores = new HashMap<>();
scores.put("Alice", 95);
scores.put("Bob", 87);
scores.put("Charlie", 92);

// Iterating over map
for (Map.Entry<String, Integer> entry : scores.entrySet()) {
    System.out.println(entry.getKey() + ": " + entry.getValue());
}
\`\`\`

## Advanced Operations

### Sorting Collections
\`\`\`java
List<String> names = Arrays.asList("Charlie", "Alice", "Bob");
Collections.sort(names); // Natural order
Collections.sort(names, Collections.reverseOrder()); // Reverse

// Custom comparator
List<Person> people = new ArrayList<>();
people.sort(Comparator.comparing(Person::getName));
\`\`\`

## Best Practices

1. **Choose the right collection** based on your needs
2. **Use interfaces** for declarations: \`List<String> list = new ArrayList<>();\`
3. **Consider thread-safety** when working with concurrent access
4. **Use generic types** for type safety
    `,
    duration: '90 minutes',
    difficulty: 'intermediate' as const,
    order: 1,
    estimatedTime: '90 minutes',
    objectives: [
      'Understand the Collections Framework hierarchy',
      'Work with Lists, Sets, and Maps effectively',
      'Apply sorting and searching operations',
      'Choose appropriate collection types for different scenarios'
    ],
    learningObjectives: [
      'Understand the Collections Framework hierarchy',
      'Work with Lists, Sets, and Maps effectively',
      'Apply sorting and searching operations',
      'Choose appropriate collection types for different scenarios'
    ],
    quiz: {
      questions: [
        {
          id: 'java-collections-1',
          question: 'Which collection type should you use when you need to maintain insertion order and allow duplicates?',
          options: [
            'HashSet',
            'TreeSet',
            'ArrayList',
            'HashMap'
          ],
          correctAnswer: 2,
          correctAnswerIndex: 2,
          difficulty: 'easy' as const,
          explanation: 'ArrayList maintains insertion order and allows duplicate elements, making it ideal for this scenario.',
          category: 'collections',
          points: 15,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-collections-2',
          question: 'What is the main difference between HashMap and TreeMap?',
          options: [
            'HashMap allows null keys, TreeMap does not',
            'HashMap is faster but unordered, TreeMap maintains sorted order',
            'HashMap is thread-safe, TreeMap is not',
            'TreeMap allows duplicates, HashMap does not'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'medium' as const,
          explanation: 'HashMap provides O(1) operations but no ordering guarantees, while TreeMap maintains sorted order with O(log n) operations.',
          category: 'collections',
          points: 20,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 70,
      timeLimit: 300,
      category: 'java'
    }
  },
  {
    id: 'java-exceptions',
    title: 'Advanced Exception Handling',
    description: 'Learn advanced exception handling techniques including custom exceptions and best practices.',
    content: `
# Advanced Exception Handling in Java

Exception handling is crucial for building robust applications that can gracefully handle errors.

## Exception Hierarchy

\`\`\`java
// Checked exceptions - must be caught or declared
try {
    FileReader file = new FileReader("data.txt");
} catch (FileNotFoundException e) {
    System.err.println("File not found: " + e.getMessage());
}

// Unchecked exceptions - runtime errors
try {
    int result = 10 / 0;
} catch (ArithmeticException e) {
    System.err.println("Cannot divide by zero");
}
\`\`\`

## Custom Exceptions

\`\`\`java
// Creating custom exception
public class InsufficientFundsException extends Exception {
    private double amount;
    
    public InsufficientFundsException(double amount) {
        super("Insufficient funds. Required: " + amount);
        this.amount = amount;
    }
    
    public double getAmount() {
        return amount;
    }
}

// Using custom exception
public void withdraw(double amount) throws InsufficientFundsException {
    if (balance < amount) {
        throw new InsufficientFundsException(amount - balance);
    }
    balance -= amount;
}
\`\`\`

## Try-with-Resources

\`\`\`java
// Automatic resource management
try (BufferedReader reader = new BufferedReader(new FileReader("file.txt"))) {
    String line;
    while ((line = reader.readLine()) != null) {
        System.out.println(line);
    }
} catch (IOException e) {
    e.printStackTrace();
}
// Reader is automatically closed
\`\`\`

## Best Practices

1. **Catch specific exceptions** before general ones
2. **Use try-with-resources** for resource management
3. **Don't catch Throwable** - catch Exception instead
4. **Log exceptions** with context information
5. **Create custom exceptions** for domain-specific errors
    `,
    duration: '75 minutes',
    difficulty: 'intermediate' as const,
    order: 2,
    estimatedTime: '75 minutes',
    objectives: [
      'Master exception handling mechanisms',
      'Create and use custom exceptions',
      'Implement try-with-resources',
      'Apply exception handling best practices'
    ],
    learningObjectives: [
      'Master exception handling mechanisms',
      'Create and use custom exceptions',
      'Implement try-with-resources',
      'Apply exception handling best practices'
    ],
    quiz: {
      questions: [
        {
          id: 'java-exceptions-1',
          question: 'What is the benefit of using try-with-resources?',
          options: [
            'It makes code run faster',
            'It automatically closes resources after use',
            'It prevents all exceptions from occurring',
            'It allows catching multiple exception types'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'medium' as const,
          explanation: 'Try-with-resources automatically closes resources that implement AutoCloseable, preventing resource leaks.',
          category: 'exceptions',
          points: 20,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 70,
      timeLimit: 300,
      category: 'java'
    }
  },
  {
    id: 'java-streams',
    title: 'Streams API & Functional Programming',
    description: 'Master Java Streams API and functional programming concepts including lambda expressions and method references.',
    content: `
# Java Streams API & Functional Programming

The Streams API provides a modern, functional approach to processing collections of data.

## Lambda Expressions

\`\`\`java
// Traditional anonymous class
Runnable runnable1 = new Runnable() {
    @Override
    public void run() {
        System.out.println("Hello");
    }
};

// Lambda expression
Runnable runnable2 = () -> System.out.println("Hello");

// Lambda with parameters
Comparator<String> comparator = (s1, s2) -> s1.length() - s2.length();
\`\`\`

## Stream Operations

### Filtering and Mapping
\`\`\`java
List<String> names = Arrays.asList("Alice", "Bob", "Charlie", "David");

// Filter and map
List<String> result = names.stream()
    .filter(name -> name.length() > 3)
    .map(String::toUpperCase)
    .collect(Collectors.toList());
// Result: [ALICE, CHARLIE, DAVID]
\`\`\`

### Reduction Operations
\`\`\`java
List<Integer> numbers = Arrays.asList(1, 2, 3, 4, 5);

// Sum using reduce
int sum = numbers.stream()
    .reduce(0, (a, b) -> a + b);

// Or using sum()
int sum2 = numbers.stream()
    .mapToInt(Integer::intValue)
    .sum();
\`\`\`

### Collectors
\`\`\`java
// Grouping
Map<Integer, List<String>> byLength = names.stream()
    .collect(Collectors.groupingBy(String::length));

// Joining
String joined = names.stream()
    .collect(Collectors.joining(", "));
\`\`\`

## Parallel Streams

\`\`\`java
// Sequential processing
long count1 = list.stream()
    .filter(s -> s.length() > 3)
    .count();

// Parallel processing
long count2 = list.parallelStream()
    .filter(s -> s.length() > 3)
    .count();
\`\`\`

## Method References

\`\`\`java
// Lambda: s -> System.out.println(s)
names.forEach(System.out::println);

// Lambda: s -> s.toUpperCase()
names.stream().map(String::toUpperCase);

// Constructor reference
Supplier<ArrayList> listSupplier = ArrayList::new;
\`\`\`

## Best Practices

1. **Use streams for bulk operations** on collections
2. **Avoid side effects** in stream operations
3. **Be careful with parallel streams** - they're not always faster
4. **Prefer method references** when possible for readability
5. **Chain operations** for cleaner code
    `,
    duration: '90 minutes',
    difficulty: 'intermediate' as const,
    order: 3,
    estimatedTime: '90 minutes',
    objectives: [
      'Master lambda expressions and functional interfaces',
      'Use Stream API for data processing',
      'Apply map, filter, and reduce operations',
      'Understand parallel streams and method references'
    ],
    learningObjectives: [
      'Master lambda expressions and functional interfaces',
      'Use Stream API for data processing',
      'Apply map, filter, and reduce operations',
      'Understand parallel streams and method references'
    ],
    quiz: {
      questions: [
        {
          id: 'java-streams-1',
          question: 'What does the filter() operation do in a Stream?',
          options: [
            'Transforms each element',
            'Selects elements that match a predicate',
            'Sorts the elements',
            'Combines all elements into one'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'easy' as const,
          explanation: 'The filter() operation selects elements from the stream that match the given predicate (boolean condition).',
          category: 'streams',
          points: 15,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-streams-2',
          question: 'When should you use parallel streams?',
          options: [
            'Always, they are always faster',
            'Never, they are always slower',
            'For large datasets where operations are independent',
            'Only for simple operations'
          ],
          correctAnswer: 2,
          correctAnswerIndex: 2,
          difficulty: 'hard' as const,
          explanation: 'Parallel streams are beneficial for large datasets with independent operations. For small datasets or operations with dependencies, sequential streams may be faster.',
          category: 'streams',
          points: 25,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 70,
      timeLimit: 400,
      category: 'java'
    }
  },
  {
    id: 'java-file-io',
    title: 'File I/O and NIO.2',
    description: 'Learn file operations using modern Java NIO.2 API including Path, Files, and directory operations.',
    content: `
# Java File I/O and NIO.2

The NIO.2 API (introduced in Java 7) provides a modern approach to file and directory operations.

## Path and Files Classes

### Creating Paths
\`\`\`java
import java.nio.file.*;

// Creating Path objects
Path path1 = Paths.get("data.txt");
Path path2 = Paths.get("/home/user/documents/file.txt");
Path path3 = Paths.get("C:\\Users\\Documents\\file.txt");

// Joining paths
Path basePath = Paths.get("/home/user");
Path fullPath = basePath.resolve("documents/file.txt");
\`\`\`

### Reading Files
\`\`\`java
// Read all lines
List<String> lines = Files.readAllLines(Paths.get("data.txt"));

// Read as stream
try (Stream<String> stream = Files.lines(Paths.get("data.txt"))) {
    stream.filter(line -> line.contains("Java"))
          .forEach(System.out::println);
}

// Read bytes
byte[] bytes = Files.readAllBytes(Paths.get("image.png"));
\`\`\`

### Writing Files
\`\`\`java
// Write strings to file
List<String> lines = Arrays.asList("Line 1", "Line 2", "Line 3");
Files.write(Paths.get("output.txt"), lines);

// Write bytes
byte[] bytes = "Hello, World!".getBytes();
Files.write(Paths.get("data.bin"), bytes);

// Append to file
Files.write(Paths.get("log.txt"), 
           "New log entry\n".getBytes(),
           StandardOpenOption.APPEND);
\`\`\`

## Directory Operations

### Creating and Listing Directories
\`\`\`java
// Create directory
Files.createDirectory(Paths.get("new_folder"));

// Create directories recursively
Files.createDirectories(Paths.get("parent/child/grandchild"));

// List directory contents
try (Stream<Path> paths = Files.list(Paths.get("."))) {
    paths.filter(Files::isRegularFile)
         .forEach(System.out::println);
}

// Walk directory tree
try (Stream<Path> paths = Files.walk(Paths.get("."))) {
    paths.filter(path -> path.toString().endsWith(".java"))
         .forEach(System.out::println);
}
\`\`\`

### File Attributes
\`\`\`java
Path path = Paths.get("file.txt");

// Check file properties
boolean exists = Files.exists(path);
boolean isDirectory = Files.isDirectory(path);
boolean isReadable = Files.isReadable(path);
long size = Files.size(path);

// Get last modified time
FileTime lastModified = Files.getLastModifiedTime(path);
\`\`\`

## Watch Service

\`\`\`java
// Monitor directory for changes
WatchService watchService = FileSystems.getDefault().newWatchService();
Path path = Paths.get("/home/user/watched");

path.register(watchService, 
             StandardWatchEventKinds.ENTRY_CREATE,
             StandardWatchEventKinds.ENTRY_DELETE,
             StandardWatchEventKinds.ENTRY_MODIFY);

// Poll for events
WatchKey key;
while ((key = watchService.take()) != null) {
    for (WatchEvent<?> event : key.pollEvents()) {
        System.out.println("Event: " + event.kind() + 
                         " - File: " + event.context());
    }
    key.reset();
}
\`\`\`

## Best Practices

1. **Use try-with-resources** for streams and buffers
2. **Use Files class** for modern file operations
3. **Handle exceptions** properly (IOException)
4. **Use buffering** for large file operations
5. **Close resources** to prevent file locks
    `,
    duration: '75 minutes',
    difficulty: 'intermediate' as const,
    order: 4,
    estimatedTime: '75 minutes',
    objectives: [
      'Use Path and Files classes for file operations',
      'Perform directory traversal and manipulation',
      'Implement file watching with WatchService',
      'Apply NIO.2 best practices'
    ],
    learningObjectives: [
      'Use Path and Files classes for file operations',
      'Perform directory traversal and manipulation',
      'Implement file watching with WatchService',
      'Apply NIO.2 best practices'
    ],
    quiz: {
      questions: [
        {
          id: 'java-file-io-1',
          question: 'What is the advantage of using Files.lines() over Files.readAllLines()?',
          options: [
            'Files.lines() is faster',
            'Files.lines() returns a Stream, allowing lazy loading of large files',
            'Files.lines() can read binary files',
            'Files.lines() automatically closes the file'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'medium' as const,
          explanation: 'Files.lines() returns a Stream that lazily loads file content, making it memory-efficient for large files. Files.readAllLines() loads the entire file into memory.',
          category: 'file-io',
          points: 20,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-file-io-2',
          question: 'Which method should you use to create nested directories in one call?',
          options: [
            'Files.createDirectory()',
            'Files.createDirectories()',
            'Files.mkdir()',
            'Files.mkdirs()'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'easy' as const,
          explanation: 'Files.createDirectories() creates the directory along with any necessary parent directories that don\'t exist.',
          category: 'file-io',
          points: 15,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 70,
      timeLimit: 300,
      category: 'java'
    }
  },
  {
    id: 'java-jdbc',
    title: 'JDBC and Database Access',
    description: 'Master database connectivity and operations using JDBC including connections, statements, and transactions.',
    content: `
# JDBC and Database Access

JDBC (Java Database Connectivity) enables Java applications to interact with databases.

## Database Connection

### Establishing Connection
\`\`\`java
import java.sql.*;

// Load JDBC driver (automatic in JDBC 4.0+)
String url = "jdbc:mysql://localhost:3306/mydb";
String username = "root";
String password = "password";

try (Connection conn = DriverManager.getConnection(url, username, password)) {
    System.out.println("Connected to database");
    // Perform database operations
} catch (SQLException e) {
    e.printStackTrace();
}
\`\`\`

## Executing Queries

### Using Statement
\`\`\`java
try (Statement stmt = conn.createStatement()) {
    // Execute query
    ResultSet rs = stmt.executeQuery("SELECT * FROM users");
    
    while (rs.next()) {
        int id = rs.getInt("id");
        String name = rs.getString("name");
        String email = rs.getString("email");
        System.out.println(id + " - " + name + " - " + email);
    }
}
\`\`\`

### Using PreparedStatement (Recommended)
\`\`\`java
String sql = "SELECT * FROM users WHERE age > ? AND city = ?";

try (PreparedStatement pstmt = conn.prepareStatement(sql)) {
    pstmt.setInt(1, 18);
    pstmt.setString(2, "New York");
    
    ResultSet rs = pstmt.executeQuery();
    
    while (rs.next()) {
        System.out.println(rs.getString("name"));
    }
}
\`\`\`

## Data Modification

### Insert, Update, Delete
\`\`\`java
// Insert
String insertSQL = "INSERT INTO users (name, email, age) VALUES (?, ?, ?)";
try (PreparedStatement pstmt = conn.prepareStatement(insertSQL)) {
    pstmt.setString(1, "John Doe");
    pstmt.setString(2, "john@example.com");
    pstmt.setInt(3, 30);
    
    int rowsAffected = pstmt.executeUpdate();
    System.out.println(rowsAffected + " row(s) inserted");
}

// Update
String updateSQL = "UPDATE users SET age = ? WHERE id = ?";
try (PreparedStatement pstmt = conn.prepareStatement(updateSQL)) {
    pstmt.setInt(1, 31);
    pstmt.setInt(2, 1);
    pstmt.executeUpdate();
}

// Delete
String deleteSQL = "DELETE FROM users WHERE id = ?";
try (PreparedStatement pstmt = conn.prepareStatement(deleteSQL)) {
    pstmt.setInt(1, 1);
    pstmt.executeUpdate();
}
\`\`\`

## Transaction Management

\`\`\`java
try {
    // Disable auto-commit
    conn.setAutoCommit(false);
    
    // Perform multiple operations
    try (PreparedStatement pstmt1 = conn.prepareStatement("INSERT INTO accounts VALUES (?, ?)")) {
        pstmt1.setInt(1, 1);
        pstmt1.setDouble(2, 1000.00);
        pstmt1.executeUpdate();
    }
    
    try (PreparedStatement pstmt2 = conn.prepareStatement("UPDATE accounts SET balance = balance - ? WHERE id = ?")) {
        pstmt2.setDouble(1, 100.00);
        pstmt2.setInt(2, 2);
        pstmt2.executeUpdate();
    }
    
    // Commit transaction
    conn.commit();
    System.out.println("Transaction committed");
    
} catch (SQLException e) {
    // Rollback on error
    conn.rollback();
    System.out.println("Transaction rolled back");
} finally {
    conn.setAutoCommit(true);
}
\`\`\`

## Connection Pooling

\`\`\`java
// Using HikariCP (popular connection pool)
HikariConfig config = new HikariConfig();
config.setJdbcUrl("jdbc:mysql://localhost:3306/mydb");
config.setUsername("root");
config.setPassword("password");
config.setMaximumPoolSize(10);

HikariDataSource dataSource = new HikariDataSource(config);

// Get connection from pool
try (Connection conn = dataSource.getConnection()) {
    // Use connection
}
\`\`\`

## Best Practices

1. **Always use PreparedStatement** to prevent SQL injection
2. **Use try-with-resources** for automatic resource cleanup
3. **Implement connection pooling** for production applications
4. **Handle transactions** properly with commit/rollback
5. **Close resources** in correct order: ResultSet, Statement, Connection
6. **Use batch updates** for multiple similar operations
    `,
    duration: '90 minutes',
    difficulty: 'intermediate' as const,
    order: 5,
    estimatedTime: '90 minutes',
    objectives: [
      'Establish database connections using JDBC',
      'Execute queries and update operations',
      'Implement transaction management',
      'Apply JDBC best practices and security'
    ],
    learningObjectives: [
      'Establish database connections using JDBC',
      'Execute queries and update operations',
      'Implement transaction management',
      'Apply JDBC best practices and security'
    ],
    quiz: {
      questions: [
        {
          id: 'java-jdbc-1',
          question: 'Why should you use PreparedStatement instead of Statement?',
          options: [
            'PreparedStatement is faster',
            'PreparedStatement prevents SQL injection attacks',
            'PreparedStatement uses less memory',
            'Statement is deprecated'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'medium' as const,
          explanation: 'PreparedStatement prevents SQL injection by properly escaping parameters and offers performance benefits through query precompilation.',
          category: 'jdbc',
          points: 20,
          type: 'multiple-choice' as const
        },
        {
          id: 'java-jdbc-2',
          question: 'What happens if you don\'t call commit() after setAutoCommit(false)?',
          options: [
            'Changes are committed automatically',
            'Changes are rolled back when connection closes',
            'An exception is thrown',
            'The database crashes'
          ],
          correctAnswer: 1,
          correctAnswerIndex: 1,
          difficulty: 'hard' as const,
          explanation: 'When auto-commit is disabled, you must explicitly call commit(). If the connection closes without commit(), all changes are automatically rolled back.',
          category: 'jdbc',
          points: 25,
          type: 'multiple-choice' as const
        }
      ],
      passThreshold: 70,
      timeLimit: 400,
      category: 'java'
    }
  }
];

export const JAVA_INTERMEDIATE_COURSE: JavaCourse = {
  id: 'java-intermediate',
  title: 'Intermediate Java Programming',
  description: 'Advance your Java skills with collections, exception handling, and design patterns.',
  level: 'intermediate',
  totalDuration: '8 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completion of Java Fundamentals course',
    'Understanding of OOP concepts',
    'Basic Java syntax and control structures'
  ],
  learningOutcomes: [
    'Master the Java Collections Framework',
    'Implement robust exception handling strategies',
    'Apply common design patterns in Java',
    'Build more complex and maintainable applications'
  ],
  modules: JAVA_INTERMEDIATE_MODULES,
  type: 'java'
};
